import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as SuccessFillIcon } from '../../../../../../assets/icons/success_fill.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import { buildQuoteCreateRoute } from '../../../../../../shared/constants/auth';
import { CLICK_ACTION, GET_QUOTE, SOURCE_PANEL } from '../../../../../../shared/constants/tagManager';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import ProspectActionsMenu from '../../components/ProspectActionsMenu/ProspectActionsMenu';
import './PendingQuoteSection.scss';

interface IPendingQuoteSectionProps {
  prospect: IProspect;
  isNew?: boolean;
  onDismissNew: (id: number) => void;
  onMarkAsLost: (id: number) => void;
  onMarkAsInactive: (id: number) => void;
  isUpdatingStatus?: boolean;
}

const PendingQuoteSection = ({
  prospect,
  isNew = false,
  onDismissNew,
  onMarkAsLost,
  onMarkAsInactive,
  isUpdatingStatus = false
}: IPendingQuoteSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleGetQuote = (id: number) => {
    TagManagerService.pushEvent(CLICK_ACTION, GET_QUOTE, SOURCE_PANEL);
    navigate(buildQuoteCreateRoute(id));
  };

  return (
    <div className="pending-quote-section">
      {isNew && (
        <div className="pending-quote-section__nudge">
          <div className="pending-quote-section__nudge-title">
            <SuccessFillIcon className="pending-quote-section__success-icon" />
            {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.nudgeTitle)}
          </div>
          <div className="pending-quote-section__nudge-body">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.nudgeBody)}
          </div>
          <div className="pending-quote-section__nudge-actions">
            <CustomButton
              variant="primary"
              className="pending-quote-section__create-quote-btn"
              size="l"
              onClick={() => handleGetQuote(prospect.id)}
            >
              {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.createFirstQuote)}
              <RightArrowIcon className="pending-quote-section__arrow" />
            </CustomButton>
            <CustomButton variant="ghost" onClick={() => onDismissNew(prospect.id)}>
              {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.doItLater)}
            </CustomButton>
          </div>
        </div>
      )}

      {!isNew && (
        <>
          <div className="pending-quote-section__hint">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.noQuotesHint)}
          </div>
          <div className="pending-quote-section__actions">
            <div className="pending-quote-section__cta">
              <CustomButton
                variant="primary"
                className="pending-quote-section__get-quote-button"
                onClick={() => handleGetQuote(prospect.id)}
              >
                {t(TRANSLATIONS_PROSPECTS.detail.pendingQuote.getQuote)}
                <RightArrowIcon className="pending-quote-section__arrow" />
              </CustomButton>
            </div>
            <ProspectActionsMenu
              prospectId={prospect.id}
              onMarkAsLost={onMarkAsLost}
              onMarkAsInactive={onMarkAsInactive}
              disabled={isUpdatingStatus}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PendingQuoteSection;
